import { isEmpty } from '@common/utils/typeGuards';
import {
    AccessorFnColumnDefBase,
    AccessorKeyColumnDefBase,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableOptions,
    Updater,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import React from 'react';
import {
    UsePaginationResult,
    UseSearchResult,
    UseSortingResult,
} from '../../../common/hooks/query';
import { UseSelectionResult } from './useSelection';

export interface UseTableProps<TData extends { id: string }>
    extends Omit<
        TableOptions<TData>,
        | 'getCoreRowModel'
        | 'onSortingChange'
        | 'onPaginationChange'
        | 'onRowSelectionChange'
        | 'onColumnVisibilityChange'
        | 'state'
    > {
    searchState?: UseSearchResult;
    paginationState?: UsePaginationResult;
    sortingState?: UseSortingResult;
    selectionState?: UseSelectionResult<TData>;

    columnVisibilityState?: [
        VisibilityState,
        React.Dispatch<React.SetStateAction<VisibilityState>>,
    ];
    initialColumnVisibility?: VisibilityState;

    // rowSelection: RowSelectionState;
    // onRowSelectionChange: (
    //     rowSelection: RowSelectionState,
    //     displayKey?: string,
    // ) => void;

    rowCount: number;
    pageCount: number;
}

const getNewState = (updater: Updater<unknown>, newState: unknown) => {
    return typeof updater === 'function' ? updater(newState) : updater;
};

export const useTable = <TData extends { id: string }>({
    data,
    columns,
    rowCount,
    pageCount,
    // searchState,
    paginationState,
    sortingState,
    selectionState,

    columnVisibilityState,
    initialColumnVisibility = {},

    getRowId,
}: UseTableProps<TData>) => {
    const sorting = sortingState?.sorting;
    const onSortingChange = sortingState?.onSortingChange;
    const pagination = paginationState?.pagination;
    const onPaginationChange = paginationState?.onPaginationChange;
    // const globalFilter = searchState?.value;
    const rowSelection = selectionState?.rowSelection;
    const onRowSelectionChange = selectionState?.onRowSelectionChange;

    const [internalColumnVisibility, setInternalColumnVisibility] =
        React.useState<VisibilityState>(initialColumnVisibility);

    const [columnVisibility, setColumnVisibility] = columnVisibilityState
        ? columnVisibilityState
        : [internalColumnVisibility, setInternalColumnVisibility];

    // Set column visibility based on defaultHidden state when columnVisibility is empty (initial load or reset)
    React.useEffect(() => {
        if (isEmpty(columnVisibility)) {
            const initialVisibility: VisibilityState = columns.reduce(
                (acc, column) => {
                    const columnId: string =
                        (
                            column as AccessorKeyColumnDefBase<TData>
                        ).accessorKey?.toString() ||
                        (
                            column as AccessorFnColumnDefBase<TData>
                        ).accessorFn?.toString() ||
                        column.id ||
                        '';
                    if (columnId) {
                        acc[columnId] = !column.meta?.defaultHidden;
                    }
                    return acc;
                },
                {} as VisibilityState,
            );
            setColumnVisibility(initialVisibility);
        }
    }, [columnVisibility, columns, setColumnVisibility]);

    const table = useReactTable({
        data,
        columns,
        rowCount,
        pageCount,

        defaultColumn: {
            minSize: 70,
            size: 100,
            enableResizing: false,
            maxSize: 300,
        },
        getRowId: getRowId ? getRowId : (row) => String(row.id),

        state: {
            sorting,
            rowSelection,
            pagination,
            columnVisibility,
            // globalFilter, // ? comment if internal filtering should be disabled
        },

        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),

        manualSorting: !!sortingState,
        getSortedRowModel: sortingState ? getSortedRowModel() : undefined,
        onSortingChange: (updater) => {
            onSortingChange?.(getNewState(updater, sorting));
        },

        manualPagination: !!paginationState,
        getPaginationRowModel: paginationState
            ? getPaginationRowModel()
            : undefined,
        onPaginationChange: (updater) => {
            onPaginationChange?.(getNewState(updater, pagination));
        },

        enableRowSelection: !!selectionState,
        onRowSelectionChange: (updater) => {
            onRowSelectionChange?.(getNewState(updater, rowSelection), data);
        },

        onColumnVisibilityChange: (updater) => {
            setColumnVisibility(getNewState(updater, columnVisibility));
        },

        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        // columnResizeDirection: 'rtl',
    });

    return { table };
};
